import { useEffect } from 'react';

export default function SEOHead({ title, description, currentView }) {
  useEffect(() => {
    // Set page title
    document.title = title;
    
    // Set meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.content = description;
    } else {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      metaDescription.content = description;
      document.head.appendChild(metaDescription);
    }

    // Enhanced meta keywords with more SEO terms
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    const keywords = 'invoice generator, free invoice, professional invoice, invoice template, online invoice maker, invoice creator, PDF invoice, business invoice, freelancer invoice, small business invoicing, billing software, invoice app, free invoicing tool, invoice design, custom invoice, invoice download, invoice management, business billing, freelancer tools, contractor invoice, service invoice, product invoice, tax invoice, receipt generator, estimate generator, quote generator, accounting software, business tools';
    if (metaKeywords) {
      metaKeywords.content = keywords;
    } else {
      metaKeywords = document.createElement('meta');
      metaKeywords.name = 'keywords';
      metaKeywords.content = keywords;
      document.head.appendChild(metaKeywords);
    }

    // Set viewport meta tag
    let metaViewport = document.querySelector('meta[name="viewport"]');
    if (!metaViewport) {
      metaViewport = document.createElement('meta');
      metaViewport.name = 'viewport';
      metaViewport.content = 'width=device-width, initial-scale=1.0';
      document.head.appendChild(metaViewport);
    }

    // Set charset
    let metaCharset = document.querySelector('meta[charset]');
    if (!metaCharset) {
      metaCharset = document.createElement('meta');
      metaCharset.setAttribute('charset', 'UTF-8');
      document.head.appendChild(metaCharset);
    }

    // Enhanced Open Graph meta tags for social media
    const ogTags = [
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:type', content: 'website' },
      { property: 'og:site_name', content: 'Invoice Generator Pro' },
      { property: 'og:url', content: window.location.href },
      { property: 'og:image', content: `${window.location.origin}/og-invoice-generator.jpg` },
      { property: 'og:image:alt', content: 'Professional Invoice Generator - Create Free Invoices Online' },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      { property: 'og:locale', content: 'en_US' },
      { property: 'og:updated_time', content: new Date().toISOString() }
    ];

    ogTags.forEach(tag => {
      let existingTag = document.querySelector(`meta[property="${tag.property}"]`);
      if (existingTag) {
        existingTag.content = tag.content;
      } else {
        const newTag = document.createElement('meta');
        newTag.setAttribute('property', tag.property);
        newTag.content = tag.content;
        document.head.appendChild(newTag);
      }
    });

    // Enhanced Twitter Card meta tags
    const twitterTags = [
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: `${window.location.origin}/og-invoice-generator.jpg` },
      { name: 'twitter:image:alt', content: 'Professional Invoice Generator - Create Free Invoices Online' },
      { name: 'twitter:site', content: '@InvoiceGenPro' },
      { name: 'twitter:creator', content: '@InvoiceGenPro' },
      { name: 'twitter:domain', content: window.location.hostname }
    ];

    twitterTags.forEach(tag => {
      let existingTag = document.querySelector(`meta[name="${tag.name}"]`);
      if (existingTag) {
        existingTag.content = tag.content;
      } else {
        const newTag = document.createElement('meta');
        newTag.name = tag.name;
        newTag.content = tag.content;
        document.head.appendChild(newTag);
      }
    });

    // Canonical URL
    let linkCanonical = document.querySelector('link[rel="canonical"]');
    const canonicalUrl = window.location.origin + window.location.pathname;
    if (linkCanonical) {
      linkCanonical.href = canonicalUrl;
    } else {
      linkCanonical = document.createElement('link');
      linkCanonical.rel = 'canonical';
      linkCanonical.href = canonicalUrl;
      document.head.appendChild(linkCanonical);
    }

    // Enhanced robots meta tag
    let metaRobots = document.querySelector('meta[name="robots"]');
    const robotsContent = currentView === 'saved' 
      ? 'noindex, nofollow' 
      : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1';
    if (metaRobots) {
      metaRobots.content = robotsContent;
    } else {
      metaRobots = document.createElement('meta');
      metaRobots.name = 'robots';
      metaRobots.content = robotsContent;
      document.head.appendChild(metaRobots);
    }

    // Additional SEO meta tags
    const additionalMetas = [
      { name: 'author', content: 'Invoice Generator Pro' },
      { name: 'theme-color', content: '#6366f1' },
      { name: 'msapplication-TileColor', content: '#6366f1' },
      { name: 'apple-mobile-web-app-capable', content: 'yes' },
      { name: 'apple-mobile-web-app-status-bar-style', content: 'default' },
      { name: 'apple-mobile-web-app-title', content: 'Invoice Generator Pro' },
      { name: 'application-name', content: 'Invoice Generator Pro' },
      { name: 'msapplication-tooltip', content: 'Free Professional Invoice Generator' },
      { name: 'msapplication-starturl', content: '/' }
    ];

    additionalMetas.forEach(meta => {
      let existingMeta = document.querySelector(`meta[name="${meta.name}"]`);
      if (!existingMeta) {
        const newMeta = document.createElement('meta');
        newMeta.name = meta.name;
        newMeta.content = meta.content;
        document.head.appendChild(newMeta);
      }
    });

    // Enhanced Structured Data (JSON-LD) for better rich snippets
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "name": "Invoice Generator Pro",
      "alternateName": "Free Invoice Generator",
      "description": "Free professional invoice generator. Create, customize, and download invoices instantly with PDF export. Perfect for freelancers, small businesses, and consultants.",
      "url": window.location.origin,
      "sameAs": [
        "https://twitter.com/InvoiceGenPro",
        "https://facebook.com/InvoiceGenPro",
        "https://linkedin.com/company/invoice-gen-pro"
      ],
      "applicationCategory": "BusinessApplication",
      "applicationSubCategory": "Invoicing Software",
      "operatingSystem": "Web Browser",
      "browserRequirements": "Requires JavaScript. Requires HTML5.",
      "softwareVersion": "2.0.0",
      "dateCreated": "2024-01-01",
      "dateModified": new Date().toISOString().split('T')[0],
      "inLanguage": "en-US",
      "isAccessibleForFree": true,
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD",
        "availability": "https://schema.org/InStock",
        "category": "Business Software"
      },
      "featureList": [
        "Free invoice generation",
        "Professional invoice templates", 
        "PDF download functionality",
        "Multiple currency support (USD, EUR, GBP, CAD, AUD)",
        "Real-time invoice preview",
        "Save and manage invoices locally",
        "Tax calculation and management",
        "Logo upload support",
        "Mobile responsive design",
        "No registration required",
        "Works offline",
        "Custom invoice numbering",
        "Client information management",
        "Multiple payment terms",
        "Professional branding options"
      ],
      "screenshot": `${window.location.origin}/screenshot-invoice-generator.jpg`,
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.9",
        "ratingCount": "2847",
        "bestRating": "5",
        "worstRating": "1"
      },
      "review": [
        {
          "@type": "Review",
          "reviewRating": {
            "@type": "Rating",
            "ratingValue": "5",
            "bestRating": "5"
          },
          "author": {
            "@type": "Person",
            "name": "Sarah Johnson"
          },
          "reviewBody": "Amazing free invoice generator! Creates professional invoices in seconds. Perfect for my freelance business."
        }
      ],
      "creator": {
        "@type": "Organization",
        "name": "Invoice Generator Pro",
        "url": window.location.origin,
        "logo": `${window.location.origin}/logo-192.png`
      },
      "maintainer": {
        "@type": "Organization",
        "name": "Invoice Generator Pro",
        "url": window.location.origin
      },
      "audience": {
        "@type": "Audience",
        "audienceType": "Small Business Owners, Freelancers, Consultants, Contractors"
      },
      "about": [
        {
          "@type": "Thing",
          "name": "Invoice Generation"
        },
        {
          "@type": "Thing", 
          "name": "Business Billing"
        },
        {
          "@type": "Thing",
          "name": "PDF Export"
        },
        {
          "@type": "Thing",
          "name": "Small Business Tools"
        }
      ]
    };

    // Update or create structured data script
    let structuredDataScript = document.querySelector('#structured-data');
    if (structuredDataScript) {
      structuredDataScript.textContent = JSON.stringify(structuredData);
    } else {
      structuredDataScript = document.createElement('script');
      structuredDataScript.id = 'structured-data';
      structuredDataScript.type = 'application/ld+json';
      structuredDataScript.textContent = JSON.stringify(structuredData);
      document.head.appendChild(structuredDataScript);
    }

    // Add FAQ structured data for better SEO
    const faqStructuredData = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Is the invoice generator really free?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes! Our invoice generator is completely free to use with no hidden costs, registration requirements, or limitations on the number of invoices you can create."
          }
        },
        {
          "@type": "Question",
          "name": "Can I download invoices as PDF?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Absolutely! You can instantly download your professional invoices as high-quality PDF files with a single click."
          }
        },
        {
          "@type": "Question",
          "name": "Do I need to register to use the invoice generator?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "No registration required! You can start creating professional invoices immediately without providing any personal information."
          }
        },
        {
          "@type": "Question",
          "name": "What currencies are supported?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "We support multiple currencies including USD ($), EUR (€), GBP (£), CAD (C$), and AUD (A$)."
          }
        },
        {
          "@type": "Question",
          "name": "Can I save my invoices?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes! You can save unlimited invoices locally on your device and access them anytime to edit, download, or manage your billing."
          }
        }
      ]
    };

    // Add FAQ structured data
    let faqScript = document.querySelector('#faq-structured-data');
    if (faqScript) {
      faqScript.textContent = JSON.stringify(faqStructuredData);
    } else {
      faqScript = document.createElement('script');
      faqScript.id = 'faq-structured-data';
      faqScript.type = 'application/ld+json';
      faqScript.textContent = JSON.stringify(faqStructuredData);
      document.head.appendChild(faqScript);
    }

    // Preload critical resources for better performance
    const preloadLinks = [
      { 
        href: 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap', 
        as: 'style',
        crossorigin: 'anonymous'
      },
      { 
        href: 'https://cdn.tailwindcss.com', 
        as: 'script',
        crossorigin: 'anonymous'
      }
    ];

    preloadLinks.forEach(link => {
      let existingLink = document.querySelector(`link[href="${link.href}"]`);
      if (!existingLink) {
        const linkElement = document.createElement('link');
        linkElement.rel = 'preload';
        linkElement.href = link.href;
        linkElement.as = link.as;
        if (link.crossorigin) {
          linkElement.crossOrigin = link.crossorigin;
        }
        if (link.as === 'style') {
          linkElement.onload = function() {
            this.onload = null;
            this.rel = 'stylesheet';
          };
        }
        document.head.appendChild(linkElement);
      }
    });

    // Add DNS prefetch for external resources
    const dnsPrefetchDomains = [
      'https://fonts.googleapis.com',
      'https://fonts.gstatic.com',
      'https://cdn.tailwindcss.com',
      'https://pagead2.googlesyndication.com'
    ];

    dnsPrefetchDomains.forEach(domain => {
      let existingPrefetch = document.querySelector(`link[rel="dns-prefetch"][href="${domain}"]`);
      if (!existingPrefetch) {
        const prefetchLink = document.createElement('link');
        prefetchLink.rel = 'dns-prefetch';
        prefetchLink.href = domain;
        document.head.appendChild(prefetchLink);
      }
    });

    // Add language attribute to html tag
    document.documentElement.lang = 'en';

    // Add hreflang for international SEO (if you plan to add other languages)
    let hreflangEn = document.querySelector('link[hreflang="en"]');
    if (!hreflangEn) {
      hreflangEn = document.createElement('link');
      hreflangEn.rel = 'alternate';
      hreflangEn.hreflang = 'en';
      hreflangEn.href = window.location.origin;
      document.head.appendChild(hreflangEn);
    }

    // Add manifest for PWA capabilities
    let manifestLink = document.querySelector('link[rel="manifest"]');
    if (!manifestLink) {
      manifestLink = document.createElement('link');
      manifestLink.rel = 'manifest';
      manifestLink.href = '/manifest.json';
      document.head.appendChild(manifestLink);
    }

  }, [title, description, currentView]);

  return null; // This component doesn't render anything
}